import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import UserIcon from '../../images/common/user.svg';
import SortMark from '../Parts/SortMark';
import SearchBox from '../Parts/SearchBox';
import {
  Table,
  TableContainer,
  Thead,
  TrHead,
  Tbody,
  Th,
  TrBody,
  Td,
  IconWrapper,
  SortColumn,
  CheckboxTh,
  CheckboxTd,
} from './styles/UserTableStyles';
import { convertRole } from '../../lib/tools';

// TODO: i18n対応
/**
 * グループ詳細ダイアログにおいて、担当者一覧を表示するコンポーネント
 * 編集モード時は、チェックボックスを使用して追加するユーザーを選択できる
 * また、ユーザー名と権限に基づいてソートする機能と、ユーザー名でフィルタリングする機能を提供する
 *
 * @param {Object[]} members 表示するユーザー情報の配列。
 * @param {boolean} isEditMode 編集モードが有効かどうかを示すフラグ。
 * @param {function} onSelectedMembersChange チェックボックスの状態が変更されたときに呼ばれる関数。
 *                                           上位コンポーネントで使用する
 */
const UserTable = ({ members = [], isEditMode, onSelectedMembersChange }) => {
  const companyAllUsers = useSelector(state => state.group.operators);

  // TODO: ロジックをカスタムフックとして切り出し。
  const [tableMembers, setTableMembers] = useState([]);

  const [selectedItems, setSelectedItems] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectItem = id => {
    setSelectedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    // 編集モードと表示モードで表示する担当者が異なる。
    // 編集モードの場合は、会社に紐づく全ユーザーが表示される。
    if (isEditMode) {
      setTableMembers(companyAllUsers);
      const newSelectedItems = {};
      companyAllUsers.forEach(operator => {
        if (members.some(member => member.guid === operator.guid)) {
          newSelectedItems[operator.guid] = true;
        }
      });
      setSelectedItems(newSelectedItems);
    } else {
      // 表示モードの場合は、グループに紐づくユーザーが表示される。
      // 権限情報を追加
      const membersWithRole = members.map(member => {
        const userWithPermission = companyAllUsers.find(
          user => user.guid === member.guid,
        );
        return {
          ...member,
          role: userWithPermission ? userWithPermission.role : '',
        };
      });
      setTableMembers(membersWithRole);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, companyAllUsers, members]);

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSort = column => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    setSortColumn(column);
    setSortDirection(isAsc ? 'desc' : 'asc');
  };

  const sortedAndFilteredMembers = useMemo(() => {
    // ユーザー名による検索フィルター
    const filteredMembers = searchQuery
      ? tableMembers.filter(member =>
          member.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : [...tableMembers];

    // チェックされているユーザーを優先してソート
    filteredMembers.sort((a, b) => {
      if (selectedItems[a.guid] && !selectedItems[b.guid]) {
        return -1;
      }
      if (!selectedItems[a.guid] && selectedItems[b.guid]) {
        return 1;
      }

      // それ以外の場合は、指定された列でソート
      if (sortColumn) {
        if (a[sortColumn] < b[sortColumn]) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (a[sortColumn] > b[sortColumn]) {
          return sortDirection === 'asc' ? 1 : -1;
        }
      }

      return 0;
    });

    return filteredMembers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, tableMembers, sortColumn, sortDirection]);

  const handleSelectAll = () => {
    const newSelected = {};
    sortedAndFilteredMembers.forEach(item => {
      newSelected[item.guid] = !selectAll;
    });
    setSelectedItems(newSelected);
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (isEditMode && onSelectedMembersChange) {
      const selectedIds = Object.keys(selectedItems).filter(
        id => selectedItems[id],
      );
      onSelectedMembersChange(selectedIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedItems]);

  // 表示されているユーザーが全て選択されている場合のみ、全選択チェックボックスをチェックする
  useEffect(() => {
    const allSelected = sortedAndFilteredMembers.every(
      member => selectedItems[member.guid],
    );
    setSelectAll(allSelected);
  }, [sortedAndFilteredMembers, selectedItems]);

  return (
    <TableContainer>
      <Table>
        <Thead>
          {isEditMode && (
            <TrHead>
              <th
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <SearchBox
                  placeholder="名前を検索"
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </th>
            </TrHead>
          )}
          <TrHead>
            {isEditMode && (
              <CheckboxTh>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </CheckboxTh>
            )}
            <Th>
              <SortColumn onClick={() => handleSort('name')}>
                名前
                <SortMark
                  sort={sortColumn === 'name'}
                  order={sortDirection === 'asc' ? 'A' : 'D'}
                />
              </SortColumn>
            </Th>
            <Th>
              <SortColumn onClick={() => handleSort('role')}>
                権限
                <SortMark
                  sort={sortColumn === 'role'}
                  order={sortDirection === 'asc' ? 'A' : 'D'}
                />
              </SortColumn>
            </Th>
          </TrHead>
        </Thead>
        <Tbody>
          {sortedAndFilteredMembers.map(user => (
            <TrBody key={user.guid}>
              {isEditMode && (
                <CheckboxTd>
                  <input
                    type="checkbox"
                    checked={!!selectedItems[user.guid]}
                    onChange={() => handleSelectItem(user.guid)}
                  />
                </CheckboxTd>
              )}
              <Td>
                <IconWrapper src={UserIcon} alt="icon" />
                {user.name}
              </Td>
              <Td>{convertRole(user.role)}</Td>
            </TrBody>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

UserTable.propTypes = {
  members: PropTypes.array.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  onSelectedMembersChange: PropTypes.func,
};

UserTable.defaultProps = {
  onSelectedMembersChange: () => {},
};
export default UserTable;
